import axios from "axios";
import { Parser as SqlParser } from "node-sql-parser";
import { inferAggregateFromSelectExpression } from "./columnMappingAggregate";
import { exprContainsAggregate } from "./sqlDataSqlAlias";

const sqlParser = new SqlParser();

let tableCatalogCache = null;
let tableCatalogFetchPromise = null;

/** @returns {'sql'|'table'|null} */
export function parseVizParamJson(raw) {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return { ...raw };
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  return {};
}

export function normalizeDataSourceMode(vizParamJson) {
  const json = parseVizParamJson(vizParamJson);
  const raw = json.dataSourceMode;
  const s = String(raw || "").trim().toLowerCase();
  if (s === "sql") return "sql";
  if (s === "table") return "table";
  return null;
}

export function dataSourceModeToVizParamValue(mode) {
  return mode === "table" ? "Table" : "SQL";
}

export function normalizeSqlForParsing(sql) {
  return String(sql || "")
    .trim()
    .replace(/\bfw_where_clause\b/gi, "1=1")
    .replace(/\bfw_and_clause\b/gi, "AND 1=1");
}

export function splitSelectColumns(selectPart) {
  const columns = [];
  let depth = 0;
  let current = "";
  let inSingle = false;
  let inDouble = false;

  for (let i = 0; i < selectPart.length; i++) {
    const char = selectPart[i];
    const prev = i > 0 ? selectPart[i - 1] : "";

    if (char === "'" && !inDouble && prev !== "\\") inSingle = !inSingle;
    if (char === '"' && !inSingle && prev !== "\\") inDouble = !inDouble;

    if (!inSingle && !inDouble) {
      if (char === "(") depth++;
      if (char === ")") depth--;
      if (char === "," && depth === 0) {
        columns.push(current.trim());
        current = "";
        continue;
      }
    }
    current += char;
  }
  if (current.trim()) columns.push(current.trim());

  return columns;
}

export function findOuterSelectSql(sql) {
  const s = String(sql || "").trim();
  if (!/^\s*with\b/i.test(s)) return s;

  let depth = 0;
  let outerStart = -1;
  for (let i = 0; i < s.length; i++) {
    const ch = s[i];
    if (ch === "(") depth++;
    else if (ch === ")") {
      depth--;
      if (depth === 0) {
        const rest = s.slice(i + 1).trim();
        if (/^select\b/i.test(rest)) outerStart = i + 1;
      }
    }
  }
  return outerStart >= 0 ? s.slice(outerStart).trim() : s;
}

export function parseSelectColumnEntry(entry) {
  const trimmed = String(entry || "").trim();
  if (!trimmed) return { expression: "", alias: null };

  const asQuoted = trimmed.match(/\bas\s+("(?:[^"]|"")*")\s*$/i);
  if (asQuoted) {
    return {
      expression: trimmed.replace(/\bas\s+("(?:[^"]|"")*")\s*$/i, "").trim(),
      alias: asQuoted[1].slice(1, -1).replace(/""/g, '"'),
    };
  }

  const asBare = trimmed.match(/\bas\s+([a-zA-Z_][\w]*)\s*$/i);
  if (asBare) {
    return {
      expression: trimmed.replace(/\bas\s+[a-zA-Z_][\w]*\s*$/i, "").trim(),
      alias: asBare[1],
    };
  }

  if (/^"[^"]+"$/.test(trimmed)) {
    return {
      expression: trimmed,
      alias: trimmed.slice(1, -1).replace(/""/g, '"'),
    };
  }

  return { expression: trimmed, alias: null };
}

export function extractOuterSelectColumnsFallback(sql) {
  const outerSelectSql = findOuterSelectSql(sql);
  const match = outerSelectSql.match(/^\s*select\s+([\s\S]+?)\s+from\b/i);
  if (!match) return [];

  return splitSelectColumns(match[1]).map((entry) =>
    parseSelectColumnEntry(entry),
  );
}

export function parseSelectAstFromSql(sql) {
  const normalized = normalizeSqlForParsing(sql);
  if (!normalized) return null;
  try {
    let ast = sqlParser.astify(normalized, { database: "PostgreSQL" });
    if (Array.isArray(ast)) ast = ast[0];
    if (!ast || ast.type !== "select" || !Array.isArray(ast.columns)) return null;
    return ast;
  } catch {
    return null;
  }
}

export function sqlifySelectColumnExpr(col, options = {}) {
  if (!col) return "";
  const includeAlias = options.includeAlias !== false;
  const columnEntry = includeAlias ? col : { expr: col.expr, as: null };
  const simpleSelectAst = {
    with: null,
    type: "select",
    options: null,
    distinct: null,
    columns: [columnEntry],
    into: { position: null },
    from: null,
    where: null,
    groupby: null,
    having: null,
    orderby: null,
    limit: null,
    locking_read: null,
    window: null,
    collate: null,
  };
  try {
    const sqlCol = sqlParser.sqlify(simpleSelectAst, { database: "PostgreSQL" });
    return sqlCol.replace(/^select\s+/i, "").trim();
  } catch {
    return "";
  }
}

export function parseSelectColumnsFromSql(sql) {
  const ast = parseSelectAstFromSql(sql);
  if (ast?.columns?.length) {
    return ast.columns.map((col) => ({
      col,
      expression: sqlifySelectColumnExpr(col, { includeAlias: false }),
      alias: col.as || null,
    }));
  }

  return extractOuterSelectColumnsFallback(sql).map((item) => ({
    col: null,
    expression: item.expression,
    alias: item.alias,
  }));
}

function normalizeSqlExprKey(expression) {
  return String(expression || "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function mappingRowByExpr(rows, expr) {
  const key = normalizeSqlExprKey(expr);
  if (!key) return null;
  return (
    (rows || []).find(
      (r) => r && normalizeSqlExprKey(r.expr) === key,
    ) || null
  );
}

function chartAliasImpliesMetric(alias) {
  const a = String(alias || "")
    .replace(/^["']|["']$/g, "")
    .trim()
    .toLowerCase();
  return /^metric\d+$/.test(a) || /^graph\d+$/.test(a);
}

function chartAliasImpliesDimension(alias) {
  const a = String(alias || "")
    .replace(/^["']|["']$/g, "")
    .trim()
    .toLowerCase();
  return /^dim\d+_name$/.test(a);
}

function headerMapValueImpliesMetric(value) {
  const v = String(value || "")
    .trim()
    .toLowerCase();
  return v.startsWith("metric") || v.startsWith("graph");
}

/**
 * Metric vs Dimension for SQL mapping rows.
 * Uses chart aliases (metricN / dimN_name) from production SQL before strip,
 * headerMap wiring, then aggregate detection on the expression.
 */
export function resolveSqlMappingColumnType({
  item = null,
  expression = "",
  label = "",
  headerMap = {},
  prev = {},
  typeHint = null,
}) {
  if (typeHint === "Metric" || typeHint === "Dimension") {
    return typeHint;
  }

  const displayLabel = String(label || "").trim();
  const mapped = displayLabel ? headerMap[displayLabel] : null;
  if (typeof mapped === "string" && mapped.trim()) {
    if (headerMapValueImpliesMetric(mapped)) return "Metric";
    if (mapped.trim().toLowerCase().startsWith("dim")) return "Dimension";
  }

  if (item?.alias != null && String(item.alias).trim() !== "") {
    if (chartAliasImpliesMetric(item.alias)) return "Metric";
    if (chartAliasImpliesDimension(item.alias)) return "Dimension";
  }

  const expr = String(expression || item?.expression || "").trim();
  if (item?.col?.expr && exprContainsAggregate(item.col.expr)) {
    return "Metric";
  }
  if (inferAggregateFromSelectExpression(expr)) {
    return "Metric";
  }

  if (prev.type === "Metric" || prev.type === "Dimension") {
    return prev.type;
  }

  return "Dimension";
}

/**
 * Parse production datasql (with dim/metric aliases) → expression key → Type.
 */
export function buildSqlMappingTypeByExpressionMap(sql) {
  const map = new Map();
  parseSelectColumnsFromSql(sql).forEach((item) => {
    const key = normalizeSqlExprKey(item.expression);
    if (!key) return;
    map.set(
      key,
      resolveSqlMappingColumnType({
        item,
        expression: item.expression,
        headerMap: {},
      }),
    );
  });
  return map;
}

function tableRefToQualified(ref) {
  if (!ref || typeof ref !== "object") return null;
  const db = ref.db != null ? String(ref.db) : "";
  const table = ref.table != null ? String(ref.table) : "";
  if (db && table) return { schema: db, table };
  if (table && table.includes(".")) {
    const parts = table.split(".");
    if (parts.length >= 2) {
      return {
        schema: parts.slice(0, -1).join("."),
        table: parts[parts.length - 1],
      };
    }
  }
  if (table) return { schema: "", table };
  return null;
}

/** @returns {{ schema: string, table: string }|null} */
export function parsePrimaryQualifiedTable(sql) {
  const ast = parseSelectAstFromSql(sql);
  if (!ast || !Array.isArray(ast.from) || !ast.from.length) {
    const fallback = String(sql || "").match(
      /\bfrom\s+([a-zA-Z_][\w]*)\s*\.\s*([a-zA-Z_][\w]*)/i,
    );
    if (fallback) {
      return { schema: fallback[1], table: fallback[2] };
    }
    return null;
  }

  for (const fromItem of ast.from) {
    if (!fromItem) continue;
    if (fromItem.table) {
      const q = tableRefToQualified(fromItem.table);
      if (q && q.table) return q;
    }
    if (fromItem.expr && fromItem.expr.table) {
      const q = tableRefToQualified(fromItem.expr.table);
      if (q && q.table) return q;
    }
  }
  return null;
}

export function selectListIsStar(sql) {
  const ast = parseSelectAstFromSql(sql);
  if (!ast?.columns?.length) return false;
  return ast.columns.some((col) => {
    const expr = col.expr;
    if (!expr) return false;
    if (expr.type === "star") return true;
    if (expr.type === "column_ref") {
      const c = expr.column;
      return c === "*" || (Array.isArray(c) && c[c.length - 1] === "*");
    }
    return false;
  });
}

/**
 * Strip SELECT-list aliases for user-facing SQL editors (VizletHeader SQL mode).
 * Chart aliases (dimN_name / metricN) are re-applied on save by buildVizletSqlModeColumnLayout.
 */
export function stripSelectAliasesFromSql(sql) {
  let rawSql = String(sql || "").trim();
  if (!rawSql) return "";

  rawSql = rawSql
    .replace(/\bfw_where_clause\b/gi, "")
    .replace(/\bfw_and_clause\b/gi, "")
    .replace(/\bfw_core_config_conn\b/gi, "")
    .replace(/;+\s*$/g, "")
    .trim();

  const ast = parseSelectAstFromSql(rawSql);
  if (ast && Array.isArray(ast.columns) && ast.columns.length) {
    ast.columns.forEach((col) => {
      if (col) col.as = null;
    });
    try {
      let out = sqlParser.sqlify(ast, { database: "PostgreSQL" });
      return out.replace(/\n\s*\n/g, "\n").trim();
    } catch {
      /* regex fallback below */
    }
  }

  return stripSelectAliasesFromSqlRegex(rawSql);
}

function stripSelectAliasesFromSqlRegex(sql) {
  const selectMatch = sql.match(/select([\s\S]*?)from/i);
  if (!selectMatch) return sql.trim();

  const selectPart = selectMatch[1];
  const afterFrom = sql.substring(
    selectMatch.index + selectMatch[0].length - 4,
  );

  const columns = selectPart
    .replace(/\n/g, " ")
    .match(/(?:[^,(]|\([^)]*\))+/g);

  if (!columns) return sql.trim();

  const aliasMap = {};
  const newColumns = [];

  columns.forEach((col) => {
    const column = col.trim();
    const aliasMatch = column.match(
      /\bas\s+(?:"([^"]+)"|'([^']+)'|([a-zA-Z_][\w$]*))\s*$/i,
    );

    if (aliasMatch) {
      const alias = aliasMatch[1] || aliasMatch[2] || aliasMatch[3];
      const original = column
        .replace(/\bas\s+(?:"[^"]+"|'[^']+'|[a-zA-Z_][\w$]*)\s*$/i, "")
        .trim();
      aliasMap[alias] = original;
      newColumns.push(original);
    } else {
      newColumns.push(column);
    }
  });

  let updatedAfterFrom = afterFrom;
  Object.keys(aliasMap).forEach((alias) => {
    const original = aliasMap[alias];
    const esc = alias.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    updatedAfterFrom = updatedAfterFrom.replace(new RegExp(`"${esc}"`, "gi"), original);
    updatedAfterFrom = updatedAfterFrom.replace(new RegExp(`\\b${esc}\\b`, "gi"), original);
  });

  const finalSql = `select ${newColumns.join(", ")} ${updatedAfterFrom}`;
  return finalSql.replace(/\n\s*\n/g, "\n").trim();
}

export function restoreFwSqlPlaceholders(sql, originalSql) {
  let out = String(sql || "");
  const original = String(originalSql || "");
  if (/\bfw_and_clause\b/i.test(original)) {
    out = out.replace(/\s+and\s+1\s*=\s*1\b/gi, " fw_and_clause");
  }
  if (/\bfw_where_clause\b/i.test(original)) {
    out = out.replace(/\bwhere\s+1\s*=\s*1\b/gi, "where fw_where_clause");
  }
  return out;
}

/** Client-side read-only SELECT guard (menu-config validateSQL). */
export function validateReadOnlySelectSql(sql) {
  if (!sql || !sql.trim()) {
    return { ok: false, message: "SQL query cannot be empty" };
  }

  const trimmed = sql.trim();
  const sqlNoTrailingSemi = trimmed.replace(/;+\s*$/g, "");
  const forbiddenKeywords = [
    "update",
    "delete",
    "truncate",
    "drop",
    "alter",
    "insert",
    "create",
    "grant",
    "revoke",
  ];
  const lowerSql = sqlNoTrailingSemi.toLowerCase();

  if (lowerSql.includes(";")) {
    return { ok: false, message: "Multiple SQL statements are not allowed" };
  }

  for (const keyword of forbiddenKeywords) {
    const regex = new RegExp(`\\b${keyword}\\b`, "i");
    if (regex.test(sql)) {
      return {
        ok: false,
        message: `"${keyword.toUpperCase()}" operations are not allowed`,
      };
    }
  }

  if (!/^\s*(select|with)\b/i.test(sqlNoTrailingSemi)) {
    return { ok: false, message: "Only SELECT queries are allowed" };
  }

  return { ok: true, message: "" };
}

export function columnNameToDisplayName(columnName) {
  const raw = String(columnName || "").trim();
  if (!raw) return "";
  return raw
    .split("_")
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

/** Strip SQL quotes from a simple column expression for display-name inference. */
export function bareColumnNameFromExpression(expr) {
  const e = String(expr || "").trim();
  const quoted = /^"((?:[^"]|"")*)"$/.exec(e);
  if (quoted) return quoted[1].replace(/""/g, '"');
  return e;
}

/**
 * Display label for a parsed SQL column row.
 * Keeps prev.label only when the expression is unchanged (user rename);
 * otherwise recomputes so partial typing (e.g. "d" → "d1l3_name") does not stick.
 */
export function resolveSqlMappingRowLabel(prev, item, expression) {
  const expr = String(expression || "").trim();
  const prevExpr = String(prev?.expr || prev?.expression || "").trim();
  const prevLabel = prev?.label != null ? String(prev.label).trim() : "";

  if (
    prevLabel &&
    prevExpr &&
    prevExpr.toLowerCase() === expr.toLowerCase()
  ) {
    return prevLabel;
  }

  if (item?.alias) {
    const alias = bareColumnNameFromExpression(String(item.alias).trim());
    return columnNameToDisplayName(alias) || alias;
  }

  const bare = bareColumnNameFromExpression(expr);
  return columnNameToDisplayName(bare) || bare;
}

export function inferDisplayTypeFromSqlType(dataType) {
  const dt = (dataType || "").toLowerCase();
  if (
    dt.includes("fw_timestamp") ||
    dt.includes("timestamp") ||
    dt === "date" ||
    dt === "datetime" ||
    /\b(date|time)\b/.test(dt)
  ) {
    return "Date";
  }
  if (
    /\bserial\d+\b/.test(dt) ||
    /\b(int|integer|bigint|smallint|numeric|decimal|float|real|double|money|serial|bigserial|number)\b/.test(
      dt,
    )
  ) {
    return "Metric";
  }
  return "Dimension";
}

export function normalizeTableModeDisplayType(catalogColumnType) {
  const raw = String(catalogColumnType || "").trim().toLowerCase();
  if (
    raw === "metric" ||
    raw === "numeric" ||
    raw === "fw_numeric" ||
    raw === "fw_core_etl.fw_num_metric" ||
    raw === "fw_core_etl.fw_num_metric_long" ||
    raw === "fw_core.fw_num_metric"
  ) {
    return "Metric";
  }
  if (raw === "date") return "Date";
  if (raw === "identifier") return "Identifier";
  return "Dimension";
}

/**
 * Metric vs Dimension for table-mode mapping rows.
 * Uses reportHeaderColumnDp display-name wiring, allHeaderMap alias, then catalog type.
 */
export function resolveTableModeDisplayType({
  catalogColumnType = "",
  headerMap = {},
  displayName = "",
  headerDisplayKey = "",
  aliasKey = "",
  prevDisplayType = "",
} = {}) {
  for (const label of [headerDisplayKey, displayName]) {
    const t = String(label || "").trim();
    if (!t) continue;
    const hk = headerMapKeyIgnoreCase(headerMap, t);
    const mapped = hk ? headerMap[hk] : null;
    if (typeof mapped === "string" && mapped.trim()) {
      if (headerMapValueImpliesMetric(mapped)) return "Metric";
      if (mapped.trim().toLowerCase().startsWith("dim")) return "Dimension";
    }
  }

  if (aliasKey) {
    if (chartAliasImpliesMetric(aliasKey)) return "Metric";
    if (chartAliasImpliesDimension(aliasKey)) return "Dimension";
  }

  const fromCatalog = normalizeTableModeDisplayType(catalogColumnType);
  if (fromCatalog !== "Dimension") return fromCatalog;

  const prev = String(prevDisplayType || "").trim();
  if (
    prev === "Metric" ||
    prev === "Dimension" ||
    prev === "Date" ||
    prev === "Identifier"
  ) {
    return prev;
  }

  return fromCatalog;
}

export async function fetchTableCatalogCached(force = false) {
  if (!force && tableCatalogCache) return tableCatalogCache;
  if (!force && tableCatalogFetchPromise) {
    return tableCatalogFetchPromise;
  }
  tableCatalogFetchPromise = axios
    .get("/api/table-catalog")
    .then(({ data }) => {
      tableCatalogCache =
        data && typeof data === "object" && !Array.isArray(data) ? data : {};
      return tableCatalogCache;
    })
    .catch(() => {
      tableCatalogCache = {};
      return tableCatalogCache;
    })
    .finally(() => {
      tableCatalogFetchPromise = null;
    });
  return tableCatalogFetchPromise;
}

export function clearTableCatalogCache() {
  tableCatalogCache = null;
  tableCatalogFetchPromise = null;
}

export function normalizeDisplayKey(value) {
  return String(value || "").trim().toLowerCase();
}

/** True when a visible chip base matches the mapping row display name. */
export function isDisplayNameInVisibleColumns(displayName, visibleColumns) {
  const key = normalizeDisplayKey(displayName);
  if (!key) return false;
  return (visibleColumns || []).some(
    (col) => col && normalizeDisplayKey(col.base) === key,
  );
}

export function filterMappingRowsBySearch(rows, searchQuery, mode) {
  const q = String(searchQuery || "")
    .trim()
    .toLowerCase();
  if (!q) return rows || [];
  return (rows || []).filter((row) => {
    if (!row) return false;
    if (mode === "table") {
      const name = String(row.columnName || "").toLowerCase();
      const label = String(row.displayName || "").toLowerCase();
      return name.includes(q) || label.includes(q);
    }
    const expr = String(row.expr || "").toLowerCase();
    const label = String(row.label || "").toLowerCase();
    const req = String(row.required || "").toLowerCase();
    return expr.includes(q) || label.includes(q) || req.includes(q);
  });
}

function resolveAliasKey(meta) {
  if (meta == null) return "";
  if (typeof meta === "string") return meta;
  if (typeof meta === "object" && typeof meta.aliasKey === "string") {
    return meta.aliasKey;
  }
  return "";
}

/** Base display label for a graph companion key (e.g. Sales# → Sales). */
export function graphCompanionBaseLabel(label) {
  const s = String(label || "");
  if (!s.endsWith("#")) return null;
  return s.replace(/#+$/, "").trim() || null;
}

export function isGraphHeaderAlias(value) {
  return (
    typeof value === "string" && value.trim().toLowerCase().startsWith("graph")
  );
}

/** Case-insensitive key lookup in reportHeaderColumnDp (e.g. m1001 → M1001). */
function headerMapKeyIgnoreCase(headerMap, label) {
  const target = String(label || "").trim();
  if (!target) return null;
  const map = headerMap || {};
  if (Object.prototype.hasOwnProperty.call(map, target)) return target;
  const lower = target.toLowerCase();
  for (const k of Object.keys(map)) {
    if (String(k).trim().toLowerCase() === lower) return k;
  }
  return null;
}

function findGraphCompanionKey(headerMap, baseLabel) {
  const base = String(baseLabel || "").trim();
  if (!base) return null;
  const exact = `${base}#`;
  if (Object.prototype.hasOwnProperty.call(headerMap || {}, exact)) return exact;
  const lower = base.toLowerCase();
  for (const k of Object.keys(headerMap || {})) {
    if (!String(k).endsWith("#")) continue;
    const companionBase = graphCompanionBaseLabel(k);
    if (companionBase && companionBase.toLowerCase() === lower) return k;
  }
  return null;
}

/** True when headerMap has baseLabel# → graphN. */
export function headerMapHasGraphCompanion(headerMap, baseLabel) {
  const key = findGraphCompanionKey(headerMap, baseLabel);
  return key != null && isGraphHeaderAlias(headerMap[key]);
}

function chartAliasSlotIndex(alias, kind) {
  const re = kind === "metric" ? /^metric(\d+)$/i : /^graph(\d+)$/i;
  const m = re.exec(String(alias || "").trim());
  return m ? parseInt(m[1], 10) : null;
}

/**
 * Graph toggle for SQL mapping rows from reportHeaderColumnDp.
 * metricN and graphN share the same index (e.g. M1001 → metric1, M1001# → graph1).
 */
export function resolveSqlMappingRowGraph(
  headerMap = {},
  {
    label = "",
    headerDisplayKey = "",
    metricAlias = "",
    aliasKey = "",
    prevGraph = "",
  } = {},
) {
  const map = headerMap || {};
  const bases = [];
  for (const s of [headerDisplayKey, label]) {
    const t = String(s || "").trim();
    if (t && !bases.includes(t)) bases.push(t);
  }

  for (const base of bases) {
    if (headerMapHasGraphCompanion(map, base)) return "yes";
  }

  let ma = String(metricAlias || "").trim();
  if (!ma && aliasKey && String(aliasKey).toLowerCase().startsWith("metric")) {
    ma = String(aliasKey).trim();
  }
  if (!ma) {
    for (const base of bases) {
      const hk = headerMapKeyIgnoreCase(map, base);
      const v = hk ? map[hk] : null;
      if (typeof v === "string" && v.trim().toLowerCase().startsWith("metric")) {
        ma = v.trim();
        break;
      }
    }
  }

  const mi = chartAliasSlotIndex(ma, "metric");
  if (mi != null) {
    const graphVal = `graph${mi}`;
    for (const k of Object.keys(map)) {
      if (!String(k).endsWith("#")) continue;
      if (String(map[k] || "").trim().toLowerCase() !== graphVal) continue;
      const companionBase = graphCompanionBaseLabel(k);
      if (!companionBase) continue;
      const baseKey = headerMapKeyIgnoreCase(map, companionBase);
      const baseMetric = baseKey
        ? String(map[baseKey] || "")
            .trim()
            .toLowerCase()
        : "";
      if (baseMetric === `metric${mi}`) return "yes";
    }
  }

  const pg = String(prevGraph || "").toLowerCase();
  return pg.startsWith("y") ? "yes" : "no";
}

/**
 * Logical table-bar column keys: one column per metric.
 * When graph is enabled, hide the base metric label (text) and keep label# only.
 */
export function tableBarVisibleHeaderKeys(headerMap) {
  const map = headerMap || {};
  return Object.keys(map).filter((k) => {
    const v = map[k];
    if (v == null || v === "") return false;
    if (String(k).endsWith("#")) return true;
    if (
      typeof v === "string" &&
      v.toLowerCase().startsWith("metric") &&
      headerMapHasGraphCompanion(map, k)
    ) {
      return false;
    }
    return true;
  });
}

export function tableBarHeaderDisplayLabel(headerKey) {
  return String(headerKey || "").replace(/#+$/, "");
}

/** One chip per logical column (VizletHeader visibleColumns). */
export function chipsFromReportHeaderMap(headerMap) {
  const map = headerMap || {};
  return tableBarVisibleHeaderKeys(map).map((k) => ({
    base: graphCompanionBaseLabel(k) || k,
    alias: map[k],
  }));
}

function findHeaderDisplayKeyForCatalogRow(
  headerMap,
  catalogBase,
  aliasKey,
  isMetricColumn,
  metricAlias,
  graphAlias,
) {
  if (!headerMap || typeof headerMap !== "object") return null;
  const catalog = String(catalogBase || "").trim();
  if (catalog) {
    if (
      Object.prototype.hasOwnProperty.call(headerMap, catalog) &&
      headerMap[catalog]
    ) {
      return catalog;
    }
    const ciKey = headerMapKeyIgnoreCase(headerMap, catalog);
    if (ciKey && !String(ciKey).endsWith("#") && headerMap[ciKey]) {
      return ciKey;
    }
    const displayFromCatalog = columnNameToDisplayName(catalog);
    if (displayFromCatalog && displayFromCatalog !== catalog) {
      const dk = headerMapKeyIgnoreCase(headerMap, displayFromCatalog);
      if (dk && !String(dk).endsWith("#") && headerMap[dk]) {
        return dk;
      }
    }
  }
  const matchVals = new Set();
  if (aliasKey) matchVals.add(aliasKey);
  if (isMetricColumn) {
    if (metricAlias) matchVals.add(metricAlias);
    if (graphAlias) matchVals.add(graphAlias);
  }
  const matchLower = new Set(
    [...matchVals].map((v) => String(v).toLowerCase()),
  );
  let graphCompanionMatch = null;
  for (const dk of Object.keys(headerMap)) {
    const v = headerMap[dk];
    if (v == null || v === "" || !matchLower.has(String(v).toLowerCase())) {
      continue;
    }
    if (String(dk).endsWith("#")) {
      graphCompanionMatch = graphCompanionMatch || dk;
      continue;
    }
    return dk;
  }
  if (graphCompanionMatch) {
    return graphCompanionBaseLabel(graphCompanionMatch) || graphCompanionMatch;
  }
  return null;
}

function metricAliasesFromAliasKey(aliasKey) {
  let metricAlias = aliasKey;
  let graphAlias = null;
  let isMetricColumn = false;
  if (typeof aliasKey === "string" && aliasKey) {
    const lower = aliasKey.toLowerCase();
    if (lower.startsWith("metric")) {
      graphAlias = "graph" + aliasKey.slice("metric".length);
      isMetricColumn = true;
    } else if (lower.startsWith("graph")) {
      graphAlias = aliasKey;
      metricAlias = "metric" + aliasKey.slice("graph".length);
      isMetricColumn = true;
    }
  }
  return { metricAlias, graphAlias, isMetricColumn };
}

/**
 * Build table-mode mapping rows from catalog + optional header merge.
 */
export function buildTableModeMappingRows({
  catalog,
  schema,
  table,
  datasql = "",
  headerMap = {},
  allHeaderMap = {},
  existingRows = [],
  visibleColumns = null,
  /** Vizlet column manager: list every catalog column, not only SELECT list. */
  showAllCatalogColumns = false,
}) {
  const cols =
    schema && table && catalog[schema] && catalog[schema][table]
      ? catalog[schema][table]
      : [];

  let allowedNames = null;
  if (!showAllCatalogColumns && datasql && !selectListIsStar(datasql)) {
    const parsed = parseSelectColumnsFromSql(datasql);
    allowedNames = new Set();
    parsed.forEach((item) => {
      const expr = (item.expression || "").trim();
      const alias = item.alias ? String(item.alias).trim() : "";
      if (alias) allowedNames.add(alias.toLowerCase());
      const bare = expr.replace(/^"([^"]+)"$/, "$1").trim();
      if (bare) allowedNames.add(bare.toLowerCase());
      const colRef = expr.match(/^([a-zA-Z_][\w]*)$/);
      if (colRef) allowedNames.add(colRef[1].toLowerCase());
    });
  }

  let seq = 1;
  const rows = [];
  for (const c of cols) {
    if (!c || !c.name) continue;
    if (allowedNames && !allowedNames.has(String(c.name).toLowerCase())) {
      continue;
    }
    const prev =
      (existingRows || []).find(
        (r) => r && r.columnName === c.name,
      ) || {};
    let aggregate = prev.aggregate != null ? prev.aggregate : "";

    const columnName = c.name;
    const defaultDisplayName = columnNameToDisplayName(columnName);
    const aliasKey = resolveAliasKey(allHeaderMap[columnName]);
    let { metricAlias, graphAlias, isMetricColumn } =
      metricAliasesFromAliasKey(aliasKey);
    const headerDisplayKey = findHeaderDisplayKeyForCatalogRow(
      headerMap,
      columnName,
      aliasKey,
      isMetricColumn,
      metricAlias,
      graphAlias,
    );
    const isVisible =
      headerDisplayKey != null && !!headerMap[headerDisplayKey];
    const displayName =
      prev.displayName != null && String(prev.displayName).trim()
        ? prev.displayName
        : headerDisplayKey && headerDisplayKey !== columnName
          ? headerDisplayKey
          : defaultDisplayName;

    if (!isMetricColumn && headerDisplayKey && headerMap[headerDisplayKey]) {
      const headerAlias = String(headerMap[headerDisplayKey]).trim();
      if (headerMapValueImpliesMetric(headerAlias)) {
        const inferred = metricAliasesFromAliasKey(headerAlias);
        metricAlias = inferred.metricAlias;
        graphAlias = inferred.graphAlias;
        isMetricColumn = inferred.isMetricColumn;
      }
    }

    const displayType = resolveTableModeDisplayType({
      catalogColumnType: c.dataType,
      headerMap,
      displayName,
      headerDisplayKey,
      aliasKey,
      prevDisplayType: prev.displayType,
    });

    const includeFromChips =
      Array.isArray(visibleColumns) && visibleColumns.length
        ? isDisplayNameInVisibleColumns(displayName, visibleColumns)
        : null;

    let graph = prev.graph != null ? prev.graph : "no";
    const isMetricRow = displayType === "Metric" || isMetricColumn;
    const headerKeyForGraph =
      headerDisplayKey ||
      headerMapKeyIgnoreCase(headerMap, displayName) ||
      headerMapKeyIgnoreCase(headerMap, columnName);
    let metricAliasForGraph = metricAlias || aliasKey || "";
    if (!metricAliasForGraph && headerKeyForGraph && headerMap[headerKeyForGraph]) {
      metricAliasForGraph = String(headerMap[headerKeyForGraph]).trim();
    }
    if (isMetricRow) {
      graph = resolveSqlMappingRowGraph(headerMap, {
        label: displayName,
        headerDisplayKey: headerKeyForGraph || displayName,
        metricAlias: metricAliasForGraph,
        aliasKey: aliasKey || metricAliasForGraph,
        prevGraph: prev.graph,
      });
    }

    if (!aggregate && datasql) {
      const chartAlias =
        headerDisplayKey && headerMap[headerDisplayKey]
          ? String(headerMap[headerDisplayKey]).trim()
          : "";
      const parsed = parseSelectColumnsFromSql(datasql);
      for (const item of parsed) {
        const expr = (item.expression || "").trim();
        const alias = item.alias ? String(item.alias).trim() : "";
        const bare = expr.replace(/^"([^"]+)"$/, "$1").trim();
        const matches =
          (chartAlias && alias.toLowerCase() === chartAlias.toLowerCase()) ||
          bare.toLowerCase() === columnName.toLowerCase();
        if (matches) {
          const inferred = inferAggregateFromSelectExpression(expr);
          if (inferred) {
            aggregate = inferred;
            break;
          }
        }
      }
    }
    if (!aggregate) {
      if (displayType === "Metric") aggregate = "SUM";
      else if (displayType === "Date") aggregate = "YEAR";
    }

    rows.push({
      seq: seq++,
      columnName,
      include:
        includeFromChips != null
          ? includeFromChips
          : prev.include != null
            ? !!prev.include
            : isVisible,
      filterable: !!prev.filterable,
      displayName,
      dataType: c.dataType,
      displayType,
      aggregate,
      graph,
      _aliasKey: aliasKey || metricAliasForGraph || "",
      _headerDisplayKey: headerKeyForGraph || headerDisplayKey,
      _isMetricColumn: isMetricRow,
      _metricAlias: metricAlias || metricAliasForGraph || "",
      _graphAlias: graphAlias,
    });
  }
  return rows;
}

/**
 * Build SQL table-bar mapping rows from datasql SELECT list.
 */
export function buildSqlModeMappingRows({
  datasql = "",
  headerMap = {},
  allHeaderMap = {},
  existingRows = [],
  includeShowColumn = false,
  visibleColumns = null,
  aliasedDatasql = "",
}) {
  const parsed = parseSelectColumnsFromSql(datasql);
  if (!parsed.length) return [];

  const typeByExpr = aliasedDatasql
    ? buildSqlMappingTypeByExpressionMap(aliasedDatasql)
    : new Map();

  return parsed.map((item, idx) => {
    const expression = item.expression || "";
    const prevByExpr = mappingRowByExpr(existingRows, expression);
    const prev = prevByExpr || existingRows[idx] || {};
    const catalogKey = item.alias || expression;
    const provisionalLabel = resolveSqlMappingRowLabel(prev, item, expression);
    const headerAlias =
      headerMap[provisionalLabel] != null
        ? String(headerMap[provisionalLabel]).trim()
        : "";
    const aliasKey =
      resolveAliasKey(allHeaderMap[catalogKey]) ||
      resolveAliasKey(allHeaderMap[provisionalLabel]) ||
      (item.alias ? String(item.alias).replace(/^["']|["']$/g, "") : "") ||
      headerAlias ||
      "";
    const { metricAlias, graphAlias, isMetricColumn: aliasIsMetric } =
      metricAliasesFromAliasKey(aliasKey);
    const headerDisplayKey = findHeaderDisplayKeyForCatalogRow(
      headerMap,
      catalogKey,
      aliasKey || item.alias,
      aliasIsMetric,
      metricAlias,
      graphAlias,
    );
    let defaultLabel = provisionalLabel;
    if (headerDisplayKey) {
      defaultLabel = headerDisplayKey;
    } else if (
      Array.isArray(visibleColumns) &&
      visibleColumns[idx] &&
      visibleColumns[idx].base
    ) {
      defaultLabel = visibleColumns[idx].base;
    }
    const isVisible =
      headerDisplayKey != null && !!headerMap[headerDisplayKey];
    const includeFromChips =
      Array.isArray(visibleColumns) && visibleColumns.length
        ? isDisplayNameInVisibleColumns(defaultLabel, visibleColumns)
        : null;

    const type = resolveSqlMappingColumnType({
      item,
      expression,
      label: defaultLabel,
      headerMap,
      prev,
      typeHint: typeByExpr.get(normalizeSqlExprKey(expression)),
    });
    const isMetricColumn = type === "Metric" || aliasIsMetric;
    const metricAliasForGraph =
      metricAlias ||
      (defaultLabel && headerMap[defaultLabel]
        ? String(headerMap[defaultLabel]).trim()
        : "") ||
      headerAlias ||
      aliasKey ||
      "";
    const graph = isMetricColumn
      ? resolveSqlMappingRowGraph(headerMap, {
          label: defaultLabel,
          headerDisplayKey: headerDisplayKey || defaultLabel,
          metricAlias: metricAliasForGraph,
          aliasKey,
          prevGraph: prev.graph,
        })
      : "no";

    const row = {
      required: `Col${idx + 1}`,
      expr: expression,
      label: defaultLabel,
      type,
      graph,
      filterable: !!prev.filterable,
      _catalogKey: catalogKey,
      _aliasKey: aliasKey,
      _headerDisplayKey: headerDisplayKey,
      _isMetricColumn: isMetricColumn,
      _metricAlias: metricAlias,
      _graphAlias: graphAlias,
    };
    if (includeShowColumn) {
      row.show =
        includeFromChips != null
          ? includeFromChips
          : prev.show != null
            ? !!prev.show
            : Array.isArray(visibleColumns) && visibleColumns.length
              ? false
              : isVisible;
    }
    return row;
  });
}

/**
 * Map table/SQL mapping rows to VizletHeader columnSelection shape.
 */
export function columnSelectionFromMappingRows(rows, mode, headerMap) {
  if (!Array.isArray(rows)) return [];
  if (mode === "table") {
    return rows.map((row) => {
      const base = row.columnName;
      const aliasKey = row._aliasKey || "";
      const isMetricColumn = !!row._isMetricColumn;
      const headerDisplayKey =
        row._headerDisplayKey ||
        findHeaderDisplayKeyForCatalogRow(
          headerMap,
          base,
          aliasKey,
          isMetricColumn,
          row._metricAlias,
          row._graphAlias,
        );
      const checked = !!row.include;
      const isRenamed =
        checked &&
        headerDisplayKey != null &&
        String(headerDisplayKey) !== String(base);
      let modeVal = null;
      let modeSelected = false;
      if (isMetricColumn && checked) {
        modeVal =
          String(row.graph || "").toLowerCase() === "yes" ? "graph" : "metric";
        modeSelected = true;
      } else if (!isMetricColumn) {
        modeVal = "metric";
      }
      return {
        base,
        alias: aliasKey,
        actualKey: base,
        metricAlias: row._metricAlias,
        graphAlias: row._graphAlias,
        isMetricColumn,
        headerDisplayKey: headerDisplayKey || (checked ? row.displayName : null),
        isRenamed,
        mode: modeVal,
        modeSelected,
        checked,
      };
    });
  }

  return rows.map((row) => {
    const base = row._catalogKey || row.expr;
    const aliasKey = row._aliasKey || "";
    const isMetricColumn = !!row._isMetricColumn;
    const headerDisplayKey =
      row._headerDisplayKey ||
      findHeaderDisplayKeyForCatalogRow(
        headerMap,
        base,
        aliasKey,
        isMetricColumn,
        row._metricAlias,
        row._graphAlias,
      );
    const checked = row.show != null ? !!row.show : !!row._wasVisible;
    const isRenamed =
      checked &&
      headerDisplayKey != null &&
      String(headerDisplayKey) !== String(base) &&
      String(headerDisplayKey) !== String(row.label);
    let modeVal = null;
    let modeSelected = false;
    if (isMetricColumn && checked) {
      modeVal = String(row.graph || "").toLowerCase() === "yes" ? "graph" : "metric";
      modeSelected = true;
    } else if (!isMetricColumn) {
      modeVal = "metric";
    }
    return {
      base,
      alias: aliasKey,
      actualKey: row.expr,
      metricAlias: row._metricAlias,
      graphAlias: row._graphAlias,
      isMetricColumn,
      headerDisplayKey: headerDisplayKey || (checked ? row.label : null),
      isRenamed,
      mode: modeVal,
      modeSelected,
      checked,
    };
  });
}

export function applyMappingRowToColumnSelection(col, row, mode) {
  if (!col || !row) return col;
  if (mode === "table") {
    col.checked = !!row.include;
    col.headerDisplayKey = row.include
      ? row.displayName || row.columnName
      : null;
    col.isRenamed =
      col.checked && String(row.displayName) !== String(row.columnName);
    if (col.isMetricColumn) {
      col.mode = String(row.graph || "").toLowerCase() === "yes" ? "graph" : "metric";
      col.modeSelected = col.checked;
    }
    return col;
  }
  col.checked = row.show != null ? !!row.show : col.checked;
  col.headerDisplayKey = col.checked ? row.label : null;
  col.isRenamed =
    col.checked && String(row.label) !== String(row._catalogKey || row.expr);
  if (col.isMetricColumn) {
    col.mode = String(row.graph || "").toLowerCase() === "yes" ? "graph" : "metric";
    col.modeSelected = col.checked;
  }
  return col;
}

export {
  isDateAggregateValue,
  isDimensionOnlyAggregateValue,
  isDimensionAggregateValue,
  inferAggregateFromSelectExpression,
  aggregateSelectEnabledForRow,
  aggregateOptionsForRow,
  tableModeGraphSelectEnabledForRow,
  onTableColumnDisplayTypeChange,
} from "./columnMappingAggregate";
